import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useDeleteTemplate, useTemplates } from "@/api/templates";
import type { AlertTemplate } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePermissions } from "@/hooks/usePermissions";
import { TemplateEditDialog } from "./TemplateEditDialog";

export function TemplatesListPage() {
  const templates = useTemplates();
  const del = useDeleteTemplate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<AlertTemplate | null>(null);
  const rows = templates.data ?? [];
  const permissions = usePermissions();

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Alert templates</h2>
          <p className="text-sm text-muted-foreground">
            Reusable noise-alert shapes a run can fire alongside the scripted incident.
          </p>
        </div>
        <Button
          disabled={!permissions.canMutate}
          title={permissions.mutationReason ?? "Insufficient permission"}
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus size={14} /> New template
        </Button>
      </header>

      {!permissions.canMutate && (
        <p className="text-xs text-muted-foreground">
          {permissions.mutationReason ?? "Insufficient permissions for this action."}
        </p>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead className="w-32">Default rate</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  <div className="font-medium text-foreground">{t.name}</div>
                  {t.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {t.description}
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{t.id}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-mono border border-border text-muted-foreground bg-muted/40">
                    {t.default_rate_per_minute}/min
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={!permissions.canMutate}
                    title={permissions.mutationReason ?? "Insufficient permission"}
                    onClick={() => {
                      setEditing(t);
                      setOpen(true);
                    }}
                    aria-label={`Edit ${t.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    disabled={!permissions.canMutate}
                    title={permissions.mutationReason ?? "Insufficient permission"}
                    onClick={() => {
                      if (confirm(`Delete ${t.id}?`)) del.mutate(t.id);
                    }}
                    aria-label={`Delete ${t.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-12">
                  No templates yet. Click <span className="font-semibold">New template</span> to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <TemplateEditDialog open={open} onOpenChange={setOpen} existing={editing} />
    </div>
  );
}
